import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import MaterialTable from '@material-table/core';
import flowRight from 'lodash.flowright';
import { injectIntl } from 'react-intl';

import { mapTableOptions } from '../../../consts/tableOptions';
import useTableLocalization from '../../../utils/useTableLocalization';
import Optional from '../../../utils/optional';
import CustomTableTitle from '../../../partials/CustomTableTitle';
import FilterButton from '../../../partials/tableCustomButtons/FilterButton';
import AddButton from '../../../partials/tableCustomButtons/AddButton';
import { setUser } from '../../../modals/editUser/actions/setUser';
import tableColumns from './tableColumns';
import { ROLES_GROUPS } from '../../../consts/rolesIds';
import compareRolesInArray from '../../../utils/compareRolesInArray';
import {
  doesArrayHasLength,
  isDefined,
  isUndefined
} from '../../../utils/isDefined';
import useLocalTableStore from '../../../utils/useLocalTableStore';
import { actions as reduxActions } from '../reducers';
import { lockUserAction } from '../actions/lockUser';
import customTableIcons from '../../../partials/tableIcons/customTableIcons';
import CustomJobsMenu from '../../../partials/customJobsMenu/CustomJobsMenu';
import filterCustomJobs from '../../../utils/filterCustomJobs';
import { withRouter } from 'react-router-dom';
import getIdFromUrl from '../../../utils/getIdfromUrl';
import { getUsers } from '../actions/getUsers';
import withSettings from '../../../utils/withSettings';

export function UsersTable({
  users,
  intl,
  openEditUser,
  client,
  isLoading,
  userRole,
  isDomainProvided,
  columns,
  setColumns,
  lockUserAction,
  clientName,
  customJobs,
  location,
  getUsers,
  settings
}) {
  const [filtering, setFiltering] = useState(false);
  const { localization, language } = useTableLocalization();

  useEffect(() => {
    getUsers(client);
    // eslint-disable-next-line
  }, [client]);

  const tableDefaultColumns = useMemo(() => {
    return tableColumns({ client, lockUserAction, settings });
  }, [client, lockUserAction, settings]);

  const {
    pageSize,
    onChangeRowsPerPage,
    onChangeColumnHidden
  } = useLocalTableStore({
    tableName: 'Users-1',
    setColumns,
    tableDefaultColumns,
    language
  });

  const mappedJobs = useMemo(() => {
    return filterCustomJobs(customJobs, 'Users');
  }, [customJobs]);

  const isAdminMenu = useMemo(() => isUndefined(getIdFromUrl(location)), [
    location
  ]);

  const actions = useMemo(
    () => {
      let buttons = [
        {
          icon: () => <FilterButton />,
          onClick: () => {
            setFiltering(!filtering);
          },
          isFreeAction: true,
          tooltip: intl.formatMessage({ id: 'BUTTON.FILTER' })
        },
        {
          icon: () => <AddButton buttonText={'BUTTON.NEW_USER'} />,
          onClick: () => openEditUser({ clientId: client, isDomainProvided }),
          isFreeAction: true,
          hidden: compareRolesInArray(userRole, ROLES_GROUPS.CLIENT_USERS)
            ? true
            : false
        }
      ];

      if (doesArrayHasLength(mappedJobs) && !isAdminMenu) {
        buttons = [
          ...buttons,
          {
            icon: () => <CustomJobsMenu customJobs={mappedJobs} />,
            onClick: () => {},
            isFreeAction: true
          }
        ];
      }

      return buttons;
    },
    // eslint-disable-next-line
    [filtering, client, isDomainProvided, mappedJobs]
  );

  const tableOptions = useMemo(() => {
    return {
      ...mapTableOptions({ clientName, tableName: 'users' }),
      filtering,
      header: doesArrayHasLength(users),
      pageSize
    };
  }, [filtering, users, clientName, pageSize]);

  return (
    <MaterialTable
      title={
        <CustomTableTitle
          text="MENU.USERS"
          icon={<i className="fas fa-users-cog"></i>}
          isLoading={isLoading}
        ></CustomTableTitle>
      }
      icons={customTableIcons}
      actions={actions}
      columns={columns}
      data={users}
      options={tableOptions}
      localization={localization}
      onChangeColumnHidden={onChangeColumnHidden}
      onRowsPerPageChange={onChangeRowsPerPage}
    />
  );
}

const mapStatesToProps = ({ users, auth, selectClient, loader }) => {
  return {
    ...users,
    isLoading: loader.isSpinVisible,
    clientId: Optional(selectClient.selectedClient)
      .map(client => client.id)
      .or(auth.defaultClient.id),
    userRole: auth.role,
    clientName: selectClient.selectedClient.name,
    isDomainProvided: isDefined(selectClient.selectedClient.connect30_domain),
    customJobs: auth.customJobs
  };
};

const mapDispatchToProps = {
  setColumns: reduxActions.setColumns,
  openEditUser: setUser,
  lockUserAction,
  getUsers
};

export default flowRight(
  withRouter,
  withSettings,
  injectIntl,
  connect(mapStatesToProps, mapDispatchToProps)
)(UsersTable);
